import { captureMessage } from '@sentry/react';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import type { FileAttachment, LiveChatStatus } from 'api/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  onAgentConnected,
  selectChatId,
  selectIntegrationConfig,
  selectLiveChatStatus,
  setLiveChatIdAndStatus,
} from 'reducers/live-chat';
import {
  selectConversationId,
  selectFeatureFlags,
  selectIsLiveChatMode,
  selectSuncoLiveChatWidgetComponent,
} from 'reducers/workflows';
import ApiService from 'services/ApiService';
import Smooch from 'smooch';
import type { JwtPayload } from '../types';
import type { SuncoMessage } from 'embed/smooch';
import { getSmoochErrorMessage, processMessage } from 'embed/smooch';
import { setIsAgentTyping, setWaitTimeMessage } from 'reducers/live-chat/index';
import { SUNCO_WAIT_TIME_NAME, USER_LEFT_CHAT_MSG } from '../consts';
import {
  addLiveChatWidgetComponents,
  endLiveChat,
  mergeUnhandledSuncoMessages,
  sendUserReply,
  updateForethoughtLiveChatHelpdeskConversationId,
} from 'actions/live-chat';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { getExceptionMessage } from 'embed/utils';
import { useLiveChatFailure } from './useLiveChatFailure';
import { clearSmoochSession } from 'persistence/utils';

window.Smooch = Smooch;

export function getSmoochLocalStorage(integrationId: string) {
  try {
    return {
      smooch_app_user_id:
        localStorage.getItem(`${integrationId}.appUserId`) || '',
      smooch_client_id: localStorage.getItem(`${integrationId}.clientId`) || '',
      smooch_session_token:
        localStorage.getItem(`${integrationId}.sessionToken`) || '',
    };
  } catch (e) {
    return {};
  }
}

const getFirstSmoochConversation = () => {
  const conversation = Smooch.getConversations()[0];

  if (!conversation) {
    throw new Error('No Smooch conversations found');
  }

  return conversation;
};

export const useHandleSuncoInitialization = () => {
  const dispatch = useAppDispatch();
  const handleLiveChatFailure = useLiveChatFailure();
  const isLiveChatMode = useAppSelector(selectIsLiveChatMode);
  const chatId = useAppSelector(selectChatId);
  const liveChatStatus = useAppSelector(selectLiveChatStatus);
  const isTabPrimary = useAppSelector(selectIsCurrentTabPrimary);
  const suncoWidgetComponent = useAppSelector(
    selectSuncoLiveChatWidgetComponent,
  );
  const { integrationId } = useAppSelector(selectIntegrationConfig);
  const ftConversationId = useAppSelector(selectConversationId);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const keepChatOpen = featureFlags.includes('sunco_keep_chat_open_after_csat');
  // used to force the hook to re-run when sunco disconnects
  const [forceUpdateNum, forceUpdate] = useReducer(x => x + 1, 0);

  const jwtToken = useMemo(() => {
    return suncoWidgetComponent?.component_fields.jwt ?? null;
  }, [suncoWidgetComponent?.component_fields.jwt]);

  /**
   * Initializes Smooch and logs in the user if a JWT is provided.
   *
   * @returns {Promise<boolean>} - Returns whether the user was logged in or not
   */
  const initAndLogin = useCallback(
    async ({
      ftConversationId,
      integrationId,
      jwt,
    }: {
      ftConversationId: string;
      integrationId: string;
      jwt: string | null;
    }) => {
      let externalId: string | undefined = undefined;

      // decode jwt
      if (jwt) {
        try {
          // Decode jwt and extract External ID
          externalId = jwtDecode<JwtPayload>(jwt)?.external_id || '';
        } catch (error) {
          dispatch(
            sendTrackingEvent({
              error: `Sunco live chat failed to decode JWT -- ${error}`,
              event_type: 'widget-handoff-auth-failure',
              integration: 'sunco-live-chat',
              ...getSmoochLocalStorage(integrationId),
            }),
          );
        }
      }

      // init Smooch
      try {
        await Smooch.init({ integrationId: integrationId });
      } catch (error) {
        sendErrorToSentry({
          consoleError: true,
          conversationId: ftConversationId,
          error: `Sunco Live Chat Smooch.init error -- ${getSmoochErrorMessage(error)}`,
        });
      }

      // login
      if (externalId && jwt) {
        try {
          await Smooch.login(externalId, jwt);
          dispatch(
            sendTrackingEvent({
              event_type: 'widget-handoff-auth-success',
              integration: 'sunco-live-chat',
              ...getSmoochLocalStorage(integrationId),
            }),
          );
          return true;
        } catch (error) {
          dispatch(
            sendTrackingEvent({
              error: `Sunco Live Chat failed to login -- ${error}`,
              event_type: 'widget-handoff-auth-failure',
              integration: 'sunco-live-chat',
              ...getSmoochLocalStorage(integrationId),
            }),
          );
        }
      }

      return false;
    },
    [dispatch],
  );

  const destroy = useCallback(async (ftConversationId: string) => {
    try {
      await Smooch.destroy();
    } catch (error) {
      sendErrorToSentry({
        consoleError: true,
        conversationId: ftConversationId,
        error: `Sunco Live Chat Smooch.destroy error -- ${getSmoochErrorMessage(error)}`,
      });
    }
  }, []);

  const bindEvents = useCallback(
    ({
      ftConversationId,
      integrationId,
    }: {
      ftConversationId: string;
      integrationId: string;
    }) => {
      // READY
      Smooch.on('ready', () => {
        const zendeskIFrame = document.querySelector<HTMLObjectElement>(
          '#web-messenger-container',
        );
        if (zendeskIFrame) {
          zendeskIFrame.style.display = 'none';
          Smooch?.close();
        }
      });

      // AGENT
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Smooch.on('message:received', async function (message: any, data) {
        if (data.conversation.id !== chatId) {
          // Ignore messages that are not part of the current chat
          return;
        }

        // Received a message that is for updating the estimated wait time only
        if (message.displayName === SUNCO_WAIT_TIME_NAME) {
          dispatch(setWaitTimeMessage(message.text));
          return;
        }

        if (message.source.type !== 'forethought') {
          dispatch(
            onAgentConnected({
              agentIcon: message.avatarUrl,
              agentName: message.displayName,
            }),
          );
        }

        const widgetComp = processMessage(message);
        if (widgetComp) {
          await dispatch(
            addLiveChatWidgetComponents({
              components: [widgetComp],
              sendAgentMessage: true,
            }),
          );
        } else if (message.type !== 'text') {
          // report to sentry. Unsupported message type and will not be rendered
          captureMessage('Unsupported message type Sunco Live Chat', {
            extra: {
              conversationId: ftConversationId,
              integrationId: integrationId,
              message: message,
              origin: ApiService.headers['solve-origin'],
            },
            level: 'info',
          });
        }

        if (
          !keepChatOpen &&
          message.type === 'text' &&
          message?.metadata?.type === 'csat' &&
          !message.actions
        ) {
          // Automatically end the chat after 5 seconds after the "Thanks for
          // your feedback" CSAT message:
          setTimeout(() => {
            dispatch(
              endLiveChat({
                /**
                 * We don't want to send "User left the chat" message after Sunco CSAT,
                 * because it will change ticket status from "Closed" to "Open".
                 */
                shouldSendUserLeftMessage: false,
              }),
            );
          }, 5000);
        }
      });

      // USER
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Smooch.on('message:sent', function (message: any, data) {
        if (data.conversation.id !== chatId) {
          // Ignore messages that are not part of the current chat
          return;
        }

        if (message.type === 'text') {
          // Don't add user message for CSAT to avoid duplicate messages:
          if (message.payload) {
            return;
          }

          // User left chat message, ignore
          if (message.text === USER_LEFT_CHAT_MSG) {
            return null;
          }

          dispatch(
            sendUserReply({
              message: message.text,
              timestamp: message.received,
            }),
          );
        } else if (message.type === 'image' || message.type === 'file') {
          const attachments: FileAttachment[] = [
            {
              content_type: message.mediaType ?? '',
              filesize: message.mediaSize ?? 0,
              name: message.altText ?? '',
              url: message.mediaUrl ?? '',
            },
          ];
          dispatch(
            sendUserReply({
              attachments,
              message: '',
              timestamp: message.received,
            }),
          );
        }
      });

      // TYPING
      Smooch.on('typing:start', data => {
        if (data.conversation.id === chatId) {
          dispatch(setIsAgentTyping(true));
        }
      });
      Smooch.on('typing:stop', data => {
        if (data.conversation.id === chatId) {
          dispatch(setIsAgentTyping(false));
        }
      });

      // TRACKING
      Smooch.on('conversation:removed', data => {
        dispatch(
          sendTrackingEvent({
            description: `Sunco Live Chat conversation removed -- conversation ${data.conversation?.id}`,
            event_type: 'widget-smooch-event',
            integration: 'sunco-live-chat',
            ...getSmoochLocalStorage(integrationId),
          }),
        );
      });
      Smooch.on('disconnected', async data => {
        /**
         * This is a workaround to ensure that the setupConversation function is re-run
         * after the Sunco Live Chat is disconnected. It forces a re-render of the app
         * by updating the state, which triggers the useEffect hook to re-run.
         */
        forceUpdate();
        dispatch(
          sendTrackingEvent({
            description: `Sunco Live Chat disconnected -- conversation: ${data.conversation?.id}`,
            event_type: 'widget-smooch-event',
            integration: 'sunco-live-chat',
            ...getSmoochLocalStorage(integrationId),
          }),
        );
      });
      Smooch.on('reconnecting', data => {
        dispatch(
          sendTrackingEvent({
            description: `Sunco Live Chat reconnecting -- conversation: ${data.conversation?.id}`,
            event_type: 'widget-smooch-event',
            integration: 'sunco-live-chat',
            ...getSmoochLocalStorage(integrationId),
          }),
        );
      });
      Smooch.on('connected', data => {
        dispatch(
          sendTrackingEvent({
            description: `Sunco Live Chat connected -- conversation: ${data.conversation?.id}`,
            event_type: 'widget-smooch-event',
            integration: 'sunco-live-chat',
            ...getSmoochLocalStorage(integrationId),
          }),
        );
      });
    },
    [chatId, dispatch, keepChatOpen],
  );

  /**
   * Initializes Smooch, binds events, and sets up the conversation.
   *
   * @param clearSmoochStorage - If true and we fail to create a conversation,
   * clear smooch local storage and then retry the function with clearSmoochStorage
   * set to false. If false, we will call handleLiveChatFailure if unable to create a conversation.
   */
  const setupConversation = useCallback(
    async ({
      chatId,
      clearSmoochStorage,
      ftConversationId,
      integrationId,
      jwt,
      liveChatStatus,
    }: {
      chatId: string;
      clearSmoochStorage: boolean;
      ftConversationId: string;
      integrationId: string;
      jwt: string | null;
      liveChatStatus: LiveChatStatus;
    }) => {
      // inline function to reduce duplicated code
      const sendHandoffTrackingEvent = (
        ftConversationId: string,
        success: boolean,
        error?: string,
      ) => {
        dispatch(
          sendTrackingEvent({
            conversation_id: ftConversationId,
            error,
            event_type: 'widget-customer-handoff',

            // `handoffData` subobject is deprecated, keeping it for backward
            // compatibility:
            handoffData: {
              conversation_id: ftConversationId,
              integration: 'sunco-live-chat',
              success: success,
            },

            integration: 'sunco-live-chat',
            jwt: jwt,
            success,
            ...getSmoochLocalStorage(integrationId),
          }),
        );
      };
      const shouldLoadExistingConversation =
        chatId && (liveChatStatus === 'active' || liveChatStatus === 'pending');

      // Start handoff tracking event
      dispatch(
        sendTrackingEvent({
          chat_id: chatId,
          conversation_id: ftConversationId,
          event_type: 'widget-start-handoff',
          integration: 'sunco-live-chat',
          live_chat_status: liveChatStatus,
          ...getSmoochLocalStorage(integrationId),
        }),
      );

      // Destroy any previous initializations to ensure we're in a clean state
      await destroy(ftConversationId);

      // set up event handlers
      try {
        bindEvents({ ftConversationId, integrationId });
      } catch (error) {
        sendErrorToSentry({
          consoleError: true,
          conversationId: ftConversationId,
          error: `Sunco Live Chat bindEvents error -- ${getSmoochErrorMessage(error)}`,
        });
      }

      // init Smooch
      const loggedIn = await initAndLogin({
        ftConversationId,
        integrationId,
        jwt,
      });

      // When ended, still bind events so we can receive possible followup messages like CSAT.
      // But don't proceed further as we don't want to create new conversations.
      if (liveChatStatus === 'ended') {
        return;
      }

      if (shouldLoadExistingConversation) {
        // load existing conversation
        try {
          await Smooch.loadConversation(chatId);
        } catch (error) {
          sendErrorToSentry({
            consoleError: true,
            conversationId: ftConversationId,
            error: `Sunco Live Chat Smooch.loadConversation error -- ${getSmoochErrorMessage(error)}`,
          });
        }

        // merge the messages into redux widgetComponents
        try {
          const conversation = await Smooch.getConversationById(chatId);
          if (conversation) {
            dispatch(
              mergeUnhandledSuncoMessages(
                conversation.messages as SuncoMessage[],
              ),
            );
          }
        } catch (error) {
          sendErrorToSentry({
            consoleError: true,
            conversationId: ftConversationId,
            error: `Sunco Live Chat Smooch.getConversationById error -- ${getSmoochErrorMessage(error)}`,
          });
        }
      } else if (liveChatStatus !== 'pending') {
        // Create a new conversation
        try {
          /**
           * Try to create the smooch conversation, if it fails
           * because of Multi-convo being disabled then
           */
          try {
            await Smooch.createConversation({
              description: 'description',
              displayName: 'displayName',
              metadata: {
                ft_conversation_id: ftConversationId,
                isFirstTimeCustomer: true,
              },
            });
          } catch (errorCreateConversation) {
            /**
             * If Multi-convo is not enabled and the user was logged in,
             * createConversation will fail. In this case we need to pass control
             * back to Zendesk Agent Workspace. If the user was not logged in,
             * our current retry behavior of clearing local storage and retrying will
             * handle the issue.
             */
            if (
              loggedIn &&
              getSmoochErrorMessage(errorCreateConversation).includes(
                'Multi-convo must be enabled',
              )
            ) {
              try {
                const smoochConversationId = getFirstSmoochConversation().id;

                /**
                 * Update the sunco conversation metadata so that our pusher
                 * subscriptions work correctly when the ZD session ends
                 */
                await Smooch.updateConversation(smoochConversationId, {
                  metadata: {
                    ft_conversation_id: ftConversationId,
                  },
                });

                await ApiService.request('handle-sunco-multi-convo-disabled', {
                  data: {
                    conversation_id: ftConversationId,
                    sunco_conversation_id: smoochConversationId,
                  },
                });
              } catch (errorMultiConvo) {
                sendErrorToSentry({
                  consoleError: true,
                  conversationId: ftConversationId,
                  error: `Sunco Live Chat failed to handle Multi-convo disabled -- ${getExceptionMessage(errorMultiConvo)}`,
                });
                throw errorMultiConvo;
              }
            } else {
              throw errorCreateConversation;
            }
          }

          /**
           * After calling createConversation, id is found by calling getConversations
           * because the response from createConversation has TEMPORARY_CONVERSATION
           * as the id.
           */
          const smoochConversation = getFirstSmoochConversation();

          dispatch(
            setLiveChatIdAndStatus({
              chatId: smoochConversation.id,
              status: 'pending',
            }),
          );

          // update forethought_live_chat doc with the new chatId
          try {
            await dispatch(
              updateForethoughtLiveChatHelpdeskConversationId(
                smoochConversation.id,
              ),
            ).unwrap();
          } catch (err) {
            sendErrorToSentry({
              consoleError: true,
              conversationId: ftConversationId,
              error: `Sunco Live Chat updateForethoughtLiveChatHelpdeskConversationId error -- ${getExceptionMessage(err)}`,
            });
          }
        } catch (error) {
          if (clearSmoochStorage && !loggedIn) {
            dispatch(
              sendTrackingEvent({
                description: `Sunco Live Chat failed to create conversation -- ${getSmoochErrorMessage(error)} -- calling clearSmoochSession and trying again`,
                event_type: 'widget-smooch-event',
                integration: 'sunco-live-chat',
                ...getSmoochLocalStorage(integrationId),
              }),
            );
            clearSmoochSession(integrationId);
            setupConversation({
              chatId,
              clearSmoochStorage: false,
              ftConversationId,
              integrationId,
              jwt,
              liveChatStatus,
            });
          } else {
            handleLiveChatFailure({
              error: `Sunco Live Chat failed to create conversation -- ${getSmoochErrorMessage(error)}`,
              integration: 'sunco-live-chat',
            });
            return;
          }
        }

        // successful handoff
        sendHandoffTrackingEvent(ftConversationId, true);
      } else {
        // failure branch that should never be reached
        sendErrorToSentry({
          conversationId: ftConversationId,
          error:
            "Sunco Live Chat reached the failure branch with liveChatStatus 'pending'",
        });
      }
    },
    [dispatch, destroy, initAndLogin, bindEvents, handleLiveChatFailure],
  );

  useEffect(() => {
    const setupConversationTimeout = setTimeout(() => {
      // We should only initialize and connect to Smooch in the primary tab
      // other tabs should rely on persistence to sync messages
      if (!isTabPrimary) {
        return;
      }

      if (!integrationId || !ftConversationId || !isLiveChatMode) {
        return;
      }

      setupConversation({
        chatId,
        clearSmoochStorage: true,
        ftConversationId,
        integrationId,
        jwt: jwtToken,
        liveChatStatus,
      });
    }, 1000);

    return () => {
      clearTimeout(setupConversationTimeout);
    };
  }, [
    dispatch,
    isTabPrimary,
    integrationId,
    ftConversationId,
    isLiveChatMode,
    setupConversation,
    jwtToken,
    chatId,
    liveChatStatus,
    forceUpdateNum,
  ]);
};
